import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type {
  AccessRole,
  AdminAuth,
  AuthPhase,
  AuthState,
  AuthStatusTrigger,
  UserRole,
} from "@/types/auth";
import type { CreateNewStoreRequest } from "@/services/authService";

const initialState: AuthState = {
  isLoading: false,
  error: null,
  phoneNumber: null,
  channel: null,
  isOTPSent: false,
  isVerified: false,
  authenticationId: null,
  userId: null,
  roles: [],
  newUser: false,
  isAuthenticated: false,
  authPhase: "idle",
  hasUserSession: false,
  hasAuthorizedStore: false,
  statusLoading: false,
  statusChecked: false,
  statusAttemptCount: 0,
  lastStatusTrigger: null,
  adminAuth: null,
  verifiedStores: [],
  pendingRoute: null,
  currentStoreId: null,
  currentStoreInfo: null,
};

const phaseForStatus = (
  isAuthorized: boolean,
  verifiedStores: AccessRole[],
  currentStoreInfo: AccessRole | null,
): AuthPhase => {
  if (!isAuthorized) {
    return "idle";
  }

  if (
    currentStoreInfo &&
    verifiedStores.some(
      (store) =>
        store.storeId === currentStoreInfo.storeId &&
        store.storeType === currentStoreInfo.storeType &&
        store.authorized === true,
    )
  ) {
    return "authenticated";
  }

  return verifiedStores.length > 0
    ? "needsStoreSelection"
    : "needsStoreCreation";
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    sendOTPRequest: (
      state,
      action: PayloadAction<{
        phoneNumber: string;
        inviteId?: string;
        channel?: string;
      }>,
    ) => {
      state.isLoading = true;
      state.error = null;
      state.phoneNumber = action.payload.phoneNumber;
      state.channel = action.payload.channel || "whatsapp";
      state.authPhase = "sendingOtp";
      state.pendingRoute = null;
    },
    sendOTPSuccess: (
      state,
      action: PayloadAction<{ authenticationId: string }>,
    ) => {
      state.isLoading = false;
      state.isOTPSent = true;
      state.error = null;
      state.authenticationId = action.payload.authenticationId;
      state.authPhase = "otpSent";
      state.pendingRoute = "/auth/verify-otp";
    },
    sendOTPFailure: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
      state.isOTPSent = false;
      state.authPhase = "error";
    },

    verifyOTPRequest: (state, _: PayloadAction<{ otp: string }>) => {
      state.isLoading = true;
      state.error = null;
      state.authPhase = "verifyingOtp";
      state.pendingRoute = null;
    },
    verifyOTPSuccess: (
      state,
      action: PayloadAction<{
        userId: string;
        roles: UserRole[];
        newUser: boolean;
      }>,
    ) => {
      state.isLoading = false;
      state.isVerified = true;
      state.isAuthenticated = true;
      state.error = null;
      state.userId = action.payload.userId;
      state.roles = action.payload.roles;
      state.newUser = action.payload.newUser;
      state.hasUserSession = true;
      state.authPhase = "checkingStatus";
    },
    verifyOTPFailure: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
      state.isVerified = false;
      state.authPhase = "error";
    },

    resendOTPRequest: (state) => {
      state.isLoading = true;
      state.error = null;
      state.authPhase = "sendingOtp";
    },

    clearError: (state) => {
      state.error = null;
    },

    clearPendingRoute: (state) => {
      state.pendingRoute = null;
    },

    clearOTPState: (state) => {
      state.phoneNumber = null;
      state.channel = null;
      state.isOTPSent = false;
      state.isVerified = false;
      state.authenticationId = null;
      state.error = null;
      state.isLoading = false;
      state.authPhase = state.hasUserSession ? "needsStoreSelection" : "idle";
      state.pendingRoute = null;
    },

    resetAuthState: (state) => {
      Object.assign(state, initialState);
      // Keep the auth status probe marked as completed so a forced reset does
      // not immediately retrigger the status check loop on auth routes.
      state.statusChecked = true;
    },

    logout: (state) => {
      Object.assign(state, initialState);
      // Logout is an intentional terminal state for the current session. Keep
      // the status check marked complete so the login page does not spam the
      // status endpoint after a 401-triggered logout.
      state.statusChecked = true;
      state.pendingRoute = "/auth/login";
    },

    setPhoneNumber: (state, action: PayloadAction<string>) => {
      state.phoneNumber = action.payload;
    },

    checkAuthStatusRequest: (
      state,
      action: PayloadAction<{ trigger?: AuthStatusTrigger } | undefined>,
    ) => {
      const nextTrigger = action.payload?.trigger || null;
      state.statusLoading = true;
      state.error = null;
      state.authPhase = "checkingStatus";
      state.pendingRoute = null;
      if (nextTrigger !== state.lastStatusTrigger) {
        state.statusAttemptCount = 0;
      }
      state.lastStatusTrigger = nextTrigger;
    },
    checkAuthStatusSuccess: (
      state,
      action: PayloadAction<{
        isAuthorized: boolean;
        adminAuth?: AdminAuth;
        verifiedStores?: AccessRole[];
        trigger?: AuthStatusTrigger | null;
        pendingRoute?: string | null;
      }>,
    ) => {
      const verifiedStores = action.payload.verifiedStores || [];

      state.statusLoading = false;
      state.error = null;
      state.isAuthenticated = action.payload.isAuthorized;
      state.statusChecked = true;
      state.hasUserSession = action.payload.isAuthorized;
      state.adminAuth = action.payload.adminAuth || null;
      state.verifiedStores = verifiedStores;
      state.pendingRoute = action.payload.pendingRoute || null;
      state.lastStatusTrigger =
        action.payload.trigger || state.lastStatusTrigger;
      state.statusAttemptCount = 0;
      state.hasAuthorizedStore = Boolean(
        state.currentStoreInfo &&
        verifiedStores.some(
          (store) =>
            store.storeId === state.currentStoreInfo?.storeId &&
            store.storeType === state.currentStoreInfo?.storeType &&
            store.authorized === true,
        ),
      );
      state.authPhase = phaseForStatus(
        action.payload.isAuthorized,
        verifiedStores,
        state.currentStoreInfo,
      );

      if (action.payload.adminAuth?.userId) {
        state.userId = action.payload.adminAuth.userId;
      }
    },
    checkAuthStatusFailure: (
      state,
      action: PayloadAction<{
        error: string;
        trigger?: AuthStatusTrigger | null;
        attemptCount?: number;
      }>,
    ) => {
      state.statusLoading = false;
      state.error = action.payload.error;
      state.isAuthenticated = false;
      state.statusChecked = true;
      state.hasUserSession = false;
      state.adminAuth = null;
      state.verifiedStores = [];
      state.authPhase = "error";
      state.lastStatusTrigger =
        action.payload.trigger || state.lastStatusTrigger;
      state.statusAttemptCount = action.payload.attemptCount || 0;
      state.hasAuthorizedStore = false;
      state.pendingRoute = "/auth/login";
    },

    createStoreRequest: (state, _: PayloadAction<CreateNewStoreRequest>) => {
      state.isLoading = true;
      state.error = null;
      state.authPhase = "creatingStore";
      state.pendingRoute = null;
    },
    createStoreSuccess: (state) => {
      state.isLoading = false;
      state.error = null;
      state.authPhase = "checkingStatus";
    },
    createStoreFailure: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
      state.authPhase = "error";
    },

    setCurrentStore: (state, action: PayloadAction<AccessRole>) => {
      state.currentStoreId = action.payload.storeId;
      state.currentStoreInfo = action.payload;
      state.error = null;
    },
    clearCurrentStore: (state) => {
      state.currentStoreId = null;
      state.currentStoreInfo = null;
      state.hasAuthorizedStore = false;
      if (state.hasUserSession) {
        state.authPhase =
          state.verifiedStores.length > 0
            ? "needsStoreSelection"
            : "needsStoreCreation";
      }
    },
    autoSetSingleStore: (state) => {
      if (state.verifiedStores.length === 1) {
        const store = state.verifiedStores[0];
        state.currentStoreId = store.storeId;
        state.currentStoreInfo = store;
      }
    },

    authorizeStoreRequest: (state, _: PayloadAction<AccessRole>) => {
      state.isLoading = true;
      state.error = null;
      state.authPhase = "authorizingStore";
      state.pendingRoute = null;
    },
    authorizeStoreSuccess: (
      state,
      action: PayloadAction<{ storeId: string }>,
    ) => {
      state.isLoading = false;
      state.error = null;
      state.hasAuthorizedStore = true;
      state.authPhase = "authenticated";

      const storeIndex = state.verifiedStores.findIndex(
        (store) => store.storeId === action.payload.storeId,
      );
      if (storeIndex >= 0) {
        state.verifiedStores[storeIndex].authorized = true;
      }
      if (state.currentStoreInfo?.storeId === action.payload.storeId) {
        state.currentStoreInfo.authorized = true;
      }
    },
    authorizeStoreFailure: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
      state.hasAuthorizedStore = false;
      state.authPhase = "needsStoreSelection";
    },
  },
});

export const {
  sendOTPRequest,
  sendOTPSuccess,
  sendOTPFailure,
  verifyOTPRequest,
  verifyOTPSuccess,
  verifyOTPFailure,
  resendOTPRequest,
  clearError,
  clearPendingRoute,
  clearOTPState,
  resetAuthState,
  setPhoneNumber,
  logout,
  checkAuthStatusRequest,
  checkAuthStatusSuccess,
  checkAuthStatusFailure,
  createStoreRequest,
  createStoreSuccess,
  createStoreFailure,
  setCurrentStore,
  clearCurrentStore,
  autoSetSingleStore,
  authorizeStoreRequest,
  authorizeStoreSuccess,
  authorizeStoreFailure,
} = authSlice.actions;

export default authSlice.reducer;
